import React, { useState } from "react";
import {
  Table,
  Input,
  Button,
  Tag,
  Space,
  Flex,
  Avatar,
  Typography,
  Select,
} from "antd";
import {
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ManOutlined,
  SearchOutlined,
  UserAddOutlined,
  UserOutlined,
  WomanOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import type { TableProps } from "antd";
import { IEmployeeQuery, IEmployeer } from "../../models/Hrm";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import ModalCreateEmployee from "../../components/modal/hrm/ModalCreateEmployee";
import { useGetEmployees } from "../../query/HrmQuery";

const { Text } = Typography;
export const HrmEmployee = () => {
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const [query, setQuery] = React.useState<IEmployeeQuery>({
    page: 1,
    limit: 10,
  });
  const { data, isLoading } = useGetEmployees(query);

  const debouncedSearch = React.useMemo(
    () =>
      debounce(
        <K extends keyof IEmployeeQuery>(
          field: K,
          value?: IEmployeeQuery[K],
        ) => {
          setQuery((prev) => ({
            ...prev,
            [field]: value ?? undefined,
            page: 1,
          }));
        },
        300,
      ),
    [],
  );

  const handleSearch =
    <K extends keyof IEmployeeQuery>(field: K) =>
    (value?: IEmployeeQuery[K]) => {
      debouncedSearch(field, value);
    };

  const columns: TableProps<IEmployeer>["columns"] = [
    {
      title: "Mã",
      key: "employeeNo",
      fixed: "left",
      width: 70,
      ellipsis: true,
      render: (_, record) => (
        <Space className="font-bold">
          <Text strong>{record.employeeNo}</Text>
        </Space>
      ),
    },
    {
      title: "Nhân viên",
      key: "employee",
      width: 250,
      render: (_, record) => (
        <Space size="middle">
          <Avatar
            icon={<UserOutlined />}
            className="bg-blue-100 text-blue-600"
            size="large"
            src={record?.user?.avatar}
          />

          <Flex vertical>
            <Text strong className="text-sm ">
              {record?.user?.fullName}
            </Text>
            <Text className="text-[10px] font-mono tracking-tighter ">
              Email: {record?.user?.email}
            </Text>
          </Flex>
        </Space>
      ),
    },
    {
      title: "Ngày tuyển",
      width: 150,

      key: "time",
      render: (_, record) => (
        <Space className="font-bold text-xs">
          <CalendarOutlined className="text-blue-500 text-sm" />
          {dayjs(record.hiredAt).format("DD/MM/YYYY")}
        </Space>
      ),
    },
    {
      title: "Giới tính",
      key: "gender",
      render: (_, record) => (
        <Tag
          color={record.gender === "MALE" ? "blue" : "pink"}
          icon={record.gender === "MALE" ? <ManOutlined /> : <WomanOutlined />}
          className="px-2 py-1 rounded-lg font-semibold m-0 text-sm"
        >
          {record.gender === "MALE" ? "Nam" : "Nữ"}
        </Tag>
      ),
    },
    {
      title: query.phone ? `SDT (${query.phone})` : "SDT",
      key: "phone",
      filterDropdown: () => (
        <Input
          placeholder="Nhập sdt nhân viên"
          prefix={<SearchOutlined />}
          onChange={(e) => handleSearch("phone")(e.target.value)}
        />
      ),
      render: (_, record) => (
        <Tag className="px-2 py-1 rounded-lg font-semibold m-0 text-sm">
          {record.phone || NaN}
        </Tag>
      ),
    },
    {
      title:
        query.isWorking !== undefined
          ? `Trạng thái (${query.isWorking ? "Đang làm" : "Nghỉ"})`
          : "Trạng thái",
      key: "isWorking",
      filterDropdown: () => (
        <Select
          placeholder="Chọn trạng thái"
          className="w-full"
          options={[
            { label: "Đang làm", value: 1 },
            { label: "Nghỉ", value: 0 },
          ]}
          onChange={(value) => {
            handleSearch("isWorking")(value);
          }}
          allowClear
        />
      ),
      render: (_, record) => (
        <Tag
          color={record?.isWorking ? "success" : "error"}
          icon={
            record?.isWorking ? (
              <CheckCircleOutlined />
            ) : (
              <CloseCircleOutlined />
            )
          }
          className="px-2 py-1 rounded-lg font-semibold m-0 text-sm"
        >
          {record?.isWorking ? "Đang làm việc" : "Đã nghỉ việc"}
        </Tag>
      ),
    },
    {
      title: "Chi tiết",
      key: "action",
      align: "right",
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => navigate(`./${record.id}`)}>
            Chi tiết
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="flex-1  p-4 overflow-auto h-full">
      <Flex className="justify-end gap-3 mb-4" wrap="wrap">
        <Button
          type="primary"
          onClick={() => setIsEmployeeModalOpen(true)}
          icon={<UserAddOutlined />}
        >
          Tạo nhân viên mới
        </Button>
      </Flex>
      <Flex vertical gap="large" className="w-full">
        <Table
          scroll={{ x: "max-content" }}
          columns={columns}
          dataSource={data?.data}
          className="shadow-sm border overflow-hidden"
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: data?.pagination.page,
            pageSize: data?.pagination.limit,
            total: data?.pagination.total,
            onChange: (page, pageSize) => {
              setQuery({
                ...query,
                page: page,
                limit: pageSize,
              });
            },
          }}
        />
      </Flex>
      <ModalCreateEmployee
        open={isEmployeeModalOpen}
        setOpen={setIsEmployeeModalOpen}
      />
    </div>
  );
};
